import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Copy, ExternalLink } from 'lucide-react';
import { ApiError, api } from '../lib/api';
import { Alert, Badge, Button, Card, Input } from './ui';
import { Row, Toggle } from './form';

interface ExtensionRow {
  id: string;
  number: string;
  portalEnabled: boolean;
  hasPortalPassword: boolean;
  portalLastLoginAt: string | null;
}

/**
 * Web-client access for one extension.
 *
 * A third credential alongside the desk-phone and browser SIP secrets, and the reason it is
 * separate is worth keeping in view: those two authenticate devices, this one authenticates a
 * person. Resetting somebody's portal password must not knock their desk phone off the network.
 */
export function WebClientAccess({ id }: { id: string }) {
  const queryClient = useQueryClient();
  const [password, setPassword] = useState('');
  const [copied, setCopied] = useState(false);

  const { data: extension } = useQuery({
    queryKey: ['extension', id],
    queryFn: () => api.get<ExtensionRow>(`/api/v1/extensions/${id}`),
  });

  const save = useMutation({
    mutationFn: (enabled: boolean) =>
      api.put(`/api/v1/extensions/${id}/portal`, {
        enabled,
        ...(password ? { password } : {}),
      }),
    onSuccess: () => {
      setPassword('');
      void queryClient.invalidateQueries({ queryKey: ['extension', id] });
      void queryClient.invalidateQueries({ queryKey: ['extensions'] });
    },
  });

  if (!extension) return null;

  const url = `${window.location.origin}/client`;

  return (
    <Card className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-medium">Web client</h3>
          <p className="text-sm text-muted-foreground">
            Lets this person sign in at <span className="font-mono">{url}</span> to make and take
            calls in their browser.
          </p>
        </div>
        {extension.portalEnabled ? <Badge tone="success">Enabled</Badge> : <Badge>Disabled</Badge>}
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Row
          label={extension.hasPortalPassword ? 'New password' : 'Password'}
          hint={
            extension.hasPortalPassword
              ? 'Leave blank to keep the current one. Changing it signs them out everywhere.'
              : 'At least 10 characters. They sign in with their extension number and this.'
          }
        >
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            placeholder={extension.hasPortalPassword ? '••••••••••' : ''}
          />
        </Row>

        <Row label="Access" hint="Turning this off ends any session they already have open.">
          <div className="flex h-9 items-center gap-3">
            <Toggle
              checked={extension.portalEnabled}
              onChange={(enabled) => save.mutate(enabled)}
              label="Web client access"
              disabled={save.isPending}
            />
            <span className="text-sm text-muted-foreground">
              {extension.portalEnabled ? 'On' : 'Off'}
            </span>
          </div>
        </Row>
      </div>

      {save.error ? (
        <Alert>{save.error instanceof ApiError ? save.error.message : 'Could not save'}</Alert>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="secondary"
          disabled={!password || save.isPending}
          onClick={() => save.mutate(true)}
        >
          {save.isPending
            ? 'Saving…'
            : extension.hasPortalPassword
              ? 'Change password'
              : 'Set password'}
        </Button>

        <Button
          variant="ghost"
          onClick={() => {
            void navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          }}
        >
          {copied ? <Check className="mr-1.5 size-4" /> : <Copy className="mr-1.5 size-4" />}
          Copy sign-in link
        </Button>

        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center text-sm text-muted-foreground underline-offset-4 hover:underline"
        >
          Open
          <ExternalLink className="ml-1 size-3.5" />
        </a>

        {extension.portalLastLoginAt && (
          <span className="ml-auto text-xs text-muted-foreground">
            Last signed in {new Date(extension.portalLastLoginAt).toLocaleString()}
          </span>
        )}
      </div>

      {/*
        The password is never shown back, so it has to be communicated at the moment it is set.
        Saying so here is cheaper than an admin discovering it after closing the panel.
      */}
      <p className="text-xs text-muted-foreground">
        The password is stored hashed and cannot be shown again. Give it to them now, or set a new
        one later.
      </p>
    </Card>
  );
}
